import type { LeadStage, Sector, SectorType, EmployeeTier, LeadSource, LostReason, EstimatedTimeline } from '../types/lead.types';

export const STAGE_LABELS: Record<LeadStage, string> = {
  new: 'Nuovo',
  contacted: 'Contattato',
  qualified: 'Qualificato',
  proposal: 'Proposta inviata',
  negotiation: 'Negoziazione',
  won: 'Vinto',
  lost: 'Perso',
};

// Settori dell'agente di sourcing (functions/src/crmSourcingKeywords.ts,
// CrmSourcingSector) — elenco più ristretto di SECTOR_OPTIONS sotto (quello
// copre tutta la tassonomia lead, questo solo i settori per cui l'agente ha
// parole chiave curate). ID duplicati apposta, non importati dal backend —
// stessa scelta di "app indipendente" già fatta per SECTOR_OPTIONS/SWISS_CANTONS.
export const SOURCING_SECTOR_OPTIONS: { id: string; label: string }[] = [
  { id: 'legal', label: 'Legale' },
  { id: 'healthcare', label: 'Sanitario/Medicale' },
  { id: 'fiduciary', label: 'Fiduciario' },
  { id: 'banking', label: 'Bancario' },
  { id: 'insurance', label: 'Assicurativo' },
  { id: 'hr', label: 'HR/Corporate' },
];

export const SECTOR_OPTIONS: { id: Sector; label: string; type: SectorType }[] = [
  { id: 'banking', label: 'Banking', type: 'private' },
  { id: 'fiduciary', label: 'Fiduciaria', type: 'private' },
  { id: 'insurance', label: 'Assicurazioni', type: 'private' },
  { id: 'healthcare', label: 'Sanità', type: 'private' },
  { id: 'legal', label: 'Legale', type: 'private' },
  { id: 'retail', label: 'Retail', type: 'private' },
  { id: 'technology', label: 'Tecnologia', type: 'private' },
  { id: 'education', label: 'Educazione (privata)', type: 'private' },
  { id: 'hr', label: 'Risorse Umane', type: 'private' },
  { id: 'manufacturing', label: 'Manifattura', type: 'private' },
  { id: 'construction', label: 'Costruzioni', type: 'private' },
  { id: 'other', label: 'Altro (privato)', type: 'private' },
  { id: 'public_municipality', label: 'Comune', type: 'public' },
  { id: 'public_canton', label: 'Ente cantonale', type: 'public' },
  { id: 'public_hospital', label: 'Ospedale pubblico', type: 'public' },
  { id: 'public_school', label: 'Scuola pubblica', type: 'public' },
];

/** Sotto-elenco dei settori validi per un tipo (privato/pubblico) — usato per
 * filtrare la select nel form, così scegliendo "Pubblico" non compaiono più
 * i settori privati e viceversa. */
export function sectorOptionsForType(type: SectorType) {
  return SECTOR_OPTIONS.filter((s) => s.type === type);
}

// Stesso elenco/ordine di src/config/onboardingConstants.ts (SWISS_CANTONS)
// — duplicato qui, non importato, per restare un'app indipendente.
export const SWISS_CANTONS: { id: string; name: string }[] = [
  { id: 'AR', name: 'Appenzello Esterno' },
  { id: 'AI', name: 'Appenzello Interno' },
  { id: 'AG', name: 'Argovia' },
  { id: 'BL', name: 'Basilea Campagna' },
  { id: 'BS', name: 'Basilea Città' },
  { id: 'BE', name: 'Berna' },
  { id: 'FR', name: 'Friburgo' },
  { id: 'GE', name: 'Ginevra' },
  { id: 'JU', name: 'Giura' },
  { id: 'GL', name: 'Glarona' },
  { id: 'GR', name: 'Grigioni' },
  { id: 'LU', name: 'Lucerna' },
  { id: 'NE', name: 'Neuchâtel' },
  { id: 'NW', name: 'Nidvaldo' },
  { id: 'OW', name: 'Obvaldo' },
  { id: 'SG', name: 'San Gallo' },
  { id: 'SH', name: 'Sciaffusa' },
  { id: 'SO', name: 'Soletta' },
  { id: 'SZ', name: 'Svitto' },
  { id: 'TI', name: 'Ticino' },
  { id: 'TG', name: 'Turgovia' },
  { id: 'UR', name: 'Uri' },
  { id: 'VS', name: 'Vallese' },
  { id: 'VD', name: 'Vaud' },
  { id: 'ZG', name: 'Zugo' },
  { id: 'ZH', name: 'Zurigo' },
];

export const EMPLOYEE_TIER_OPTIONS: { id: EmployeeTier; label: string }[] = [
  { id: 'micro', label: 'Micro (1–9)' },
  { id: 'small', label: 'Small (10–49)' },
  { id: 'medium', label: 'Medium (50–249)' },
  { id: 'large', label: 'Large (250+)' },
];

export const SOURCE_OPTIONS: { id: LeadSource; label: string }[] = [
  { id: 'fiera', label: 'Fiera/evento' },
  { id: 'referral', label: 'Referral' },
  { id: 'cold_outreach', label: 'Cold outreach' },
  { id: 'linkedin', label: 'LinkedIn' },
  { id: 'passaparola', label: 'Passaparola' },
  { id: 'agente_registro', label: 'Agente (registro di commercio)' },
  { id: 'checkup_web', label: 'Check-up gratuito (sito)' },
  { id: 'price_estimate_web', label: 'Calcolatore prezzo (sito)' },
  { id: 'sito_web', label: 'Modulo contatti (sito)' },
  { id: 'altro', label: 'Altro' },
];

export const TIMELINE_OPTIONS: { id: EstimatedTimeline; label: string }[] = [
  { id: '<1m', label: 'Entro 1 mese' },
  { id: '1-3m', label: '1–3 mesi' },
  { id: '3-6m', label: '3–6 mesi' },
  { id: '>6m', label: 'Oltre 6 mesi' },
];

export const LOST_REASON_OPTIONS: { id: LostReason; label: string }[] = [
  { id: 'budget', label: 'Budget' },
  { id: 'timing', label: 'Momento non adatto' },
  { id: 'competitor', label: 'Scelto un concorrente' },
  { id: 'no_urgency', label: 'Nessuna urgenza di conformità' },
  { id: 'ghosting', label: 'Non risponde più' },
  { id: 'out_of_scope', label: 'Fuori target' },
  { id: 'altro', label: 'Altro' },
];
